import React, { useState, useEffect } from "react";
import Draggable from "react-draggable";
import "./ActiveFiresPanel.css";
import CloseIcon from "@mui/icons-material/Close";
import FilterNoneOutlinedIcon from "@mui/icons-material/FilterNoneOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { getLocalNowMinusOneHour } from "../../../utils/DateTimeUtils";
import {
  isoToDate,
  getNextDay,
  getPrevDay,
  dateToIso,
  isToday,
  getLocalNowTimeStr,
} from "../../../utils/activeFiresDateUtils";
import ActiveFiresContent from "../../molecules/activeFiresContent/ActiveFiresContent";

const ActiveFiresPanel = ({
  showComponent,
  handleDraggableComponentToggle,
  isLayerActive,
  onToggleLayer,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApply,
  detectedCount,
  isLoading,
  isSmallScreen,
  onShowNavbarMenu,
  reducedDragWindow = false,
  onReduceDragWindow,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  timelineHours,
  timelineCursor,
  onTimelineCursorChange,
  timelineBuckets,
  selectedPreset,
  onChangePreset,
  displayStartDate,
  onChangeDisplayStartDate,
  displayEndDate,
  onChangeDisplayEndDate,
}) => {
  const [activeDrags, setActiveDrags] = useState(0);
  const [deltaPosition, setDeltaPosition] = useState({ x: 0, y: 0 });

  // resolve end time: live current time if end date is today, else end-of-day
  const resolveEndTime = (endDateIso) =>
    isToday(endDateIso) ? getLocalNowTimeStr() : "23:59";
  const toInputDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
  };
  const toInputTime = (date) => {
    if (!date) return "";
    const h = String(date.getHours()).padStart(2, "0");
    const m = String(date.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  };
  const persoDisabled = !displayStartDate || !displayEndDate;

useEffect(() => {
    if (displayStartDate || displayEndDate) {
      onChangePreset("perso");

      if (displayStartDate) {
        // If the user hasn't picked an end date yet, default it to today —
        // matching what the DatePicker already shows visually.
        const effectiveEndDate = displayEndDate || dateToIso(new Date());
        const customStartTime = "00:00";
        const customEndTime = resolveEndTime(effectiveEndDate);

        onStartTimeChange(customStartTime);
        onEndTimeChange(customEndTime);
        onEndDateChange(effectiveEndDate); // keep the real query state in sync too

        if (!isLayerActive) onToggleLayer();
        onApply({
          startDate: displayStartDate,
          endDate: effectiveEndDate,
          startTime: customStartTime,
          endTime: customEndTime,
        });
      }
    } else if (
      selectedPreset === "perso" &&
      !displayStartDate &&
      !displayEndDate
    ) {
      onChangePreset("maintenant");
      applyMaintenant();
      if (isLayerActive) {
        onToggleLayer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayStartDate, displayEndDate]);

  const applyMaintenant = () => {
    const { date, time } = getLocalNowMinusOneHour();
    onStartDateChange(date);
    onStartTimeChange(time);
    onEndDateChange("");
    onEndTimeChange("");
    return { date, time };
  };

  const handlePreset = (preset) => {
    if (preset === "maintenant") {
      const { date, time } = applyMaintenant();
      onChangeDisplayStartDate("");
      onChangeDisplayEndDate("");
      onChangePreset("maintenant");
      if (!isLayerActive) onToggleLayer();
      onApply({ startDate: date, endDate: "", startTime: time, endTime: "" });
      return;
    }

    if (preset === "perso") {
      if (persoDisabled) return;
      onChangePreset("perso");
      return; // applied once both custom dates are picked, see effect below
    }

    const end = new Date();
    const start = new Date();
    if (preset === "24h") start.setDate(end.getDate() - 1);
    else if (preset === "72h") start.setDate(end.getDate() - 3);
    else if (preset === "7jours") start.setDate(end.getDate() - 7);

    const now = toInputTime(end);
    const startDateStr = toInputDate(start);
    const endDateStr = toInputDate(end);

    onStartDateChange(startDateStr);
    onStartTimeChange(now);
    onEndDateChange(endDateStr);
    onEndTimeChange(now);

    onChangeDisplayStartDate("");
    onChangeDisplayEndDate("");
    onChangePreset(preset);

    if (!isLayerActive) onToggleLayer();
    onApply({
      startDate: startDateStr,
      endDate: endDateStr,
      startTime: now,
      endTime: now,
    });
  };
  const handleCustomStartDateChange = (date) => {
    const iso = dateToIso(date);

    if (selectedPreset !== "perso") {
      onEndDateChange("");
      onChangeDisplayEndDate("");
      onEndTimeChange("");
    }
    onStartTimeChange(date ? "00:00" : "");

    onChangeDisplayStartDate(iso);
    onStartDateChange(iso);
  };

  const handleCustomEndDateChange = (date) => {
    const iso = dateToIso(date);

    if (selectedPreset !== "perso") {
      onStartDateChange("");
      onChangeDisplayStartDate("");
      onStartTimeChange("");
    }
    onEndTimeChange(date ? resolveEndTime(iso) : "");

    onChangeDisplayEndDate(iso);
    onEndDateChange(iso);
  };

  const handleApply = () => {
    if (!isLayerActive) onToggleLayer();
    onApply();
  };

  const handleCancel = () => {
    if (isLayerActive) onToggleLayer();
  };

  const handleStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev + 1);
  };

  const handleDrag = (e, data) => {
    e.stopPropagation();
    e.preventDefault();
    setDeltaPosition({
      x: deltaPosition.x + data.deltaX,
      y: deltaPosition.y + data.deltaY,
    });
  };

  const handleStop = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev - 1);
  };

  const returnToMenu = () => {
    handleDraggableComponentToggle();
    onShowNavbarMenu();
  };

  if (!showComponent) return null;

  /* ---------- Mobile sidebar ---------- */
  if (isSmallScreen) {
    return (
      <div className="sidebar-draggableComp active-fires-sidebar open">
        <div className="drag-comp-header">
          <KeyboardDoubleArrowLeftIcon
            onClick={returnToMenu}
            className="drag-comp-returnIcon"
            data-testid="active-fires-return-btn"
          />
          <div className="drag-comp-title" data-testid="active-fires-title">
            Points chauds
          </div>
          <CloseIcon
            onClick={handleDraggableComponentToggle}
            className="drag-comp-closeIcon"
            data-testid="active-fires-close-btn"
          />
        </div>
        <div className="drag-comp-body" data-testid="active-fires-body">
          <ActiveFiresContent
            displayStartDate={displayStartDate}
            displayEndDate={displayEndDate}
            onStartDateChange={handleCustomStartDateChange}
            onEndDateChange={handleCustomEndDateChange}
            onApply={handleApply}
            onCancel={handleCancel}
            detectedCount={detectedCount}
            isLoading={isLoading}
            selectedPreset={selectedPreset}
            onPreset={handlePreset}
            persoDisabled={persoDisabled}
            isSmallScreen={isSmallScreen}
            isLayerActive={isLayerActive}
            timelineHours={timelineHours}
            timelineCursor={timelineCursor}
            onTimelineCursorChange={onTimelineCursorChange}
            timelineBuckets={timelineBuckets}
            onToggleLayer={onToggleLayer}
          />
        </div>
      </div>
    );
  }

  /* ---------- Desktop draggable ---------- */
  return (
    <Draggable
      handle=".handle"
      defaultPosition={{ x: -50, y: 50 }}
      position={null}
      grid={[25, 25]}
      scale={1}
      onStart={handleStart}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <div
        className="draggable-comp active-fires-comp"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        data-testid="active-fires-panel"
      >
        <div className="drag-comp-header handle">
          <div className="drag-comp-title" data-testid="active-fires-title">
            Points chauds
          </div>
          <div className="drag-header-icons">
            {reducedDragWindow ? (
              <CropSquareOutlinedIcon
                onClick={onReduceDragWindow}
                className="drag-comp-closeIcon"
                style={{ fontSize: "20px" }}
                data-testid="active-fires-expand-btn"
              />
            ) : (
              <FilterNoneOutlinedIcon
                onClick={onReduceDragWindow}
                className="drag-comp-closeIcon"
                style={{ fontSize: "17px" }}
                data-testid="active-fires-reduce-btn"
              />
            )}
            <CloseIcon
              onClick={handleDraggableComponentToggle}
              className="drag-comp-closeIcon"
              style={{ fontSize: "20px" }}
              data-testid="active-fires-close-btn"
            />
          </div>
        </div>

        {!reducedDragWindow && (
          <div className="drag-comp-body" data-testid="active-fires-body">
            <ActiveFiresContent
              displayStartDate={displayStartDate}
              displayEndDate={displayEndDate}
              onStartDateChange={handleCustomStartDateChange}
              onEndDateChange={handleCustomEndDateChange}
              onApply={handleApply}
              onCancel={handleCancel}
              detectedCount={detectedCount}
              isLoading={isLoading}
              selectedPreset={selectedPreset}
              onPreset={handlePreset}
              persoDisabled={persoDisabled}
              isSmallScreen={isSmallScreen}
              isLayerActive={isLayerActive}
              timelineHours={timelineHours}
              timelineCursor={timelineCursor}
              onTimelineCursorChange={onTimelineCursorChange}
              timelineBuckets={timelineBuckets}
              onToggleLayer={onToggleLayer}
            />
          </div>
        )}
      </div>
    </Draggable>
  );
};

export default ActiveFiresPanel;
